//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Wizard Action dialog
//
//  Copyright:    © 2023 - 2025 Idox Software Limited.
//  Copyright:    © 2023 - 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001            Sean Flook                  Initial Revision.
//    002   22.09.23 Sean Flook                  Changes required to handle Scottish classifications.
//    003   06.10.23 Sean Flook                  Use colour variables.
//    004   03.11.23 Sean Flook                  Updated propTypes.
//    005   10.11.23 Sean Flook        IMANN-175 Changes required for Move BLPU seed point.
//    006   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system.
//    007   30.11.23 Sean Flook                  Added sub-locality.
//    008   05.01.24 Sean Flook                  Use CSS shortcuts.
//    009   11.01.24 Sean Flook                  Fix warnings.
//    010   16.01.24 Sean Flook                  Changes required to fix warnings.
//    011   27.02.24 Sean Flook            MUL15 Changed to use dialogTitleStyle.
//    012   27.03.24 Sean Flook                  Added ADSDialogTitle.
//    013   09.04.24 Sean Flook        IMANN-376 Allow lookups to be added on the fly.
//    014   08.05.24 Sean Flook        IMANN-447 Added exclude from export and site visit to the options of fields that can be edited.
//    015   19.06.24 Joshua McCormick  IMANN-503 BLPU Level field max characters 30 and removed up down counter.
//    016   19.06.24 Joshua McCormick  IMANN-503 BLPU Level AdsNumberControl type set to text to hide up down
//    017   19.06.24 Joshua McCormick  IMANN-503 BLPU Level removed type prop
//    018   19.06.24 Joshua McCormick  IMANN-503 BLPU Level max set to 99.9
//    019   19.06.24 Sean Flook        IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    020   16.07.24 Sean Flook        IMANN-581 Limit the RPC drop down list to values valid for new records.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    021   24.10.24 Sean Flook       IMANN-1033 Only sort and filter lookups when required.
//endregion Version 1.0.1.0
//region Version 1.0.6.0
//    022   11.07.25 Sean Flook       IMANN-1863 Change postcode to a text field.
//    023   04.08.25 Sean Flook       IMANN-1922 Added the isPostcode attribute to the postcode control.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    024   13.06.25 Sean Flook       IMANN-1812 Moved defaults.
//    025   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import SettingsContext from "../context/settingsContext";
import LookupContext from "../context/lookupContext";
import UserContext from "../context/userContext";

import AddLookupDialog from "../dialogs/AddLookupDialog";

import { addLookup, getLookupVariantString, GetLookupLabel } from "../utils/HelperUtils";
import { FilteredRepresentativePointCode } from "../utils/PropertyUtils";

import { Dialog, DialogContent, DialogActions, Button, Box } from "@mui/material";
import ADSSelectControl from "../components/ADSSelectControl";
import ADSTextControl from "../components/ADSTextControl";
import ADSNumberControl from "../components/ADSNumberControl";
import ADSDialogTitle from "../components/ADSDialogTitle";

import BLPUClassification from "../data/BLPUClassification";
import OSGClassification from "../data/OSGClassification";

import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/SaveOutlined";

import { adsMidGreyA } from "../utils/ADSColours";
import { blueButtonStyle, whiteButtonStyle } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";
import ADSSwitchControl from "../components/ADSSwitchControl";

WizardActionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf([
    "classification",
    "excludeFromExport",
    "siteVisit",
    "level",
    "postcode",
    "postTown",
    "subLocality",
    "note",
    "rpc",
  ]),
  data: PropTypes.any,
  recordCount: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

function WizardActionDialog({ open, variant, data, recordCount = 1, onClose, onCancel }) {
  const theme = useTheme();

  const settingsContext = useContext(SettingsContext);
  const lookupContext = useContext(LookupContext);
  const userContext = useContext(UserContext);

  const [representativePointCodeLookup, setRepresentativePointCodeLookup] = useState(
    FilteredRepresentativePointCode(settingsContext.isScottish, true)
  );

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [lookupType, setLookupType] = useState("unknown");
  const [engError, setEngError] = useState(null);
  const [altLanguageError, setAltLanguageError] = useState(null);

  const [postTownEngLookup, setPostTownEngLookup] = useState([]);
  const [postTownCymLookup, setPostTownCymLookup] = useState([]);
  const [subLocalityLookup, setSubLocalityLookup] = useState([]);

  const addResult = useRef(null);
  const currentVariant = useRef(null);

  const [actionData, setActionData] = useState("");
  const maxContentHeight = "240px";
  const boxStyle = { maxHeight: maxContentHeight, fontSize: "16px", color: adsMidGreyA, lineHeight: "22px" };

  /**
   * Event to handle when the cancel button is clicked.
   */
  const handleCancel = () => {
    onCancel();
  };

  /**
   * Event to handle when the OK button is clicked.
   */
  const handleOk = () => {
    onClose(actionData);
  };

  /**
   * Method to handle when the English post town is changed.
   *
   * @param {number} newValue The reference for the English post town.
   */
  const handleEngPostTownChanged = (newValue) => {
    if (settingsContext.isScottish || settingsContext.isWelsh) {
      const selectedRecord = lookupContext.currentLookups.postTowns.find((x) => x.postTownRef === newValue);
      if (selectedRecord && selectedRecord.linkedRef !== newValue)
        setActionData({ eng: newValue, alt: selectedRecord.linkedRef });
      else setActionData({ eng: newValue, alt: actionData.alt });
    } else setActionData({ eng: newValue, alt: actionData.alt });
  };

  /**
   * Method to handle when the alternative language post town is changed.
   *
   * @param {number} newValue The reference for the alternative language post town.
   */
  const handleAltPostTownChanged = (newValue) => {
    const selectedRecord = lookupContext.currentLookups.postTowns.find((x) => x.postTownRef === newValue);
    if (selectedRecord && selectedRecord.linkedRef !== newValue)
      setActionData({ eng: selectedRecord.linkedRef, alt: newValue });
    else setActionData({ eng: actionData.eng, alt: newValue });
  };

  /**
   * Event to handle when a new post town is added.
   */
  const handleAddPostTownEvent = () => {
    setLookupType("postTown");
    setShowAddDialog(true);
  };

  /**
   * Method to handle when the English sub-locality is changed.
   *
   * @param {number} newValue The reference for the English sub-locality.
   */
  const handleEngSubLocalityChanged = (newValue) => {
    const selectedRecord = lookupContext.currentLookups.subLocalities.find((x) => x.subLocalityRef === newValue);
    if (selectedRecord && selectedRecord.linkedRef !== newValue)
      setActionData({ eng: newValue, alt: selectedRecord.linkedRef });
    else setActionData({ eng: newValue, alt: actionData.alt });
  };

  /**
   * Event to handle when a new sub-locality is added.
   */
  const handleAddSubLocalityEvent = () => {
    setLookupType("subLocality");
    setShowAddDialog(true);
  };

  /**
   * Method to get the title for the dialog.
   *
   * @returns {string} The title text for the dialog.
   */
  const getTitle = () => {
    const getEditTitle = (type) => {
      return `Edit ${type} for ${
        recordCount && recordCount > 1 ? recordCount.toString() + " " : ""
      }property ${recordText}`;
    };

    const recordText = recordCount && recordCount > 1 ? "records" : "record";

    switch (variant) {
      case "classification":
        return getEditTitle("classification");

      case "excludeFromExport":
        return getEditTitle("exclude from export");

      case "siteVisit":
        return getEditTitle("site visit required");

      case "level":
        return getEditTitle("level");

      case "postcode":
        return getEditTitle("postcode");

      case "postTown":
        return getEditTitle("post town");

      case "subLocality":
        return getEditTitle("sub-locality");

      case "note":
        return `Add note for ${
          recordCount && recordCount > 1 ? recordCount.toString() + " " : ""
        }property ${recordText}`;

      case "rpc":
        return getEditTitle("representative point code");

      default:
        return "Edit record";
    }
  };

  /**
   * Method to get the required content of the dialog.
   *
   * @returns {JSX.Element} The content required for the dialog
   */
  const getContent = () => {
    switch (variant) {
      case "classification":
        return (
          <Box sx={boxStyle}>
            <ADSSelectControl
              label="Classification"
              isEditable
              isFocused
              isClassification
              includeHiddenCode
              useRounded
              doNotSetTitleCase
              lookupData={settingsContext.isScottish ? OSGClassification : BLPUClassification}
              lookupId="id"
              lookupLabel="display"
              lookupColour="colour"
              value={actionData}
              onChange={(newValue) => setActionData(newValue)}
              helperText="Classification code for the BLPU."
            />
          </Box>
        );

      case "excludeFromExport":
        return (
          <Box sx={boxStyle}>
            <ADSSwitchControl
              label="Exclude from export"
              isEditable
              isFocused
              checked={actionData}
              trueLabel="Yes"
              falseLabel="No"
              helperText="Set this if you do not want this property to be included in any exports."
              onChange={(newValue) => setActionData(newValue)}
            />
          </Box>
        );

      case "siteVisit":
        return (
          <Box sx={boxStyle}>
            <ADSSwitchControl
              label="Site visit required"
              isEditable
              isFocused
              checked={actionData}
              trueLabel="Yes"
              falseLabel="No"
              helperText="Set this if the property requires a site visit."
              onChange={(newValue) => setActionData(newValue)}
            />
          </Box>
        );

      case "level":
        return (
          <Box sx={boxStyle}>
            {settingsContext.isScottish ? (
              <ADSNumberControl
                label="Level"
                maximum={99.9}
                isEditable
                isFocused
                value={actionData}
                helperText="Code describing vertical position of BLPU."
                onChange={(newValue) => setActionData(newValue)}
              />
            ) : (
              <ADSTextControl
                label="Level"
                isEditable
                isFocused
                value={actionData}
                id="lpi_level_1"
                maxLength={30}
                helperText="Memorandum of the vertical position of the BLPU."
                onChange={(newValue) => setActionData(newValue)}
              />
            )}
          </Box>
        );

      case "postcode":
        return (
          <Box sx={boxStyle}>
            <ADSTextControl
              label="Postcode"
              isEditable
              isFocused
              isPostcode
              forceUppercase
              value={actionData}
              id="wizard_action_postcode"
              maxLength={8}
              helperText="Allocated by the Royal Mail to assist in delivery of mail."
              onChange={(newValue) => setActionData(newValue)}
            />
          </Box>
        );

      case "postTown":
        return (
          <Box sx={boxStyle}>
            <ADSSelectControl
              label={`${settingsContext.isScottish || settingsContext.isWelsh ? "English p" : "P"}ost town`}
              isEditable
              isFocused
              useRounded
              allowAddLookup
              lookupData={postTownEngLookup}
              lookupId="postTownRef"
              lookupLabel="postTown"
              value={actionData ? actionData.eng : null}
              onChange={handleEngPostTownChanged}
              onAddLookup={handleAddPostTownEvent}
              helperText="Allocated by the Royal Mail to assist in delivery of mail."
            />
            {settingsContext.isWelsh && (
              <ADSSelectControl
                label="Welsh post town"
                isEditable
                useRounded
                allowAddLookup
                lookupData={postTownCymLookup}
                lookupId="postTownRef"
                lookupLabel="postTown"
                value={actionData ? actionData.alt : null}
                onChange={handleAltPostTownChanged}
                onAddLookup={handleAddPostTownEvent}
                helperText="Allocated by the Royal Mail to assist in delivery of mail."
              />
            )}
          </Box>
        );

      case "subLocality":
        return (
          <Box sx={boxStyle}>
            <ADSSelectControl
              label={"English sub-locality"}
              isEditable
              isFocused
              useRounded
              allowAddLookup
              lookupData={subLocalityLookup}
              lookupId="subLocalityRef"
              lookupLabel="subLocality"
              value={actionData ? actionData.eng : null}
              onChange={handleEngSubLocalityChanged}
              onAddLookup={handleAddSubLocalityEvent}
              helperText="Third level of geographic area name. e.g. to record an island name or property group."
            />
          </Box>
        );

      case "note":
        return (
          <Box sx={boxStyle}>
            <ADSTextControl
              isEditable
              value={actionData}
              id="property_note"
              maxLength={4000}
              minLines={2}
              maxLines={10}
              onChange={(newValue) => setActionData(newValue)}
            />
          </Box>
        );

      case "rpc":
        return (
          <Box sx={boxStyle}>
            <ADSSelectControl
              label="RPC"
              isEditable
              isFocused
              useRounded
              doNotSetTitleCase
              lookupData={representativePointCodeLookup}
              lookupId="id"
              lookupLabel={GetLookupLabel(settingsContext.isScottish)}
              lookupColour="colour"
              value={actionData}
              onChange={(newValue) => setActionData(newValue)}
              helperText="Representative Point Code."
            />
          </Box>
        );

      default:
        break;
    }
  };

  /**
   * Method used to add the new lookup.
   *
   * @param {object} data The data returned from the add lookup dialog.
   */
  const handleDoneAddLookup = async (data) => {
    currentVariant.current = getLookupVariantString(data.variant);

    const addResults = await addLookup(
      data,
      settingsContext.authorityCode,
      userContext,
      settingsContext.isWelsh,
      lookupContext.currentLookups
    );

    if (addResults && addResults.result) {
      if (addResults.updatedLookups && addResults.updatedLookups.length > 0)
        lookupContext.onUpdateLookup(data.variant, addResults.updatedLookups);

      switch (data.variant) {
        case "postTown":
          handleEngPostTownChanged(addResults.newLookup.postTownRef);
          break;

        case "subLocality":
          handleEngSubLocalityChanged(addResults.newLookup.subLocalityRef);
          break;

        default:
          break;
      }

      addResult.current = true;
    } else addResult.current = false;
    setEngError(addResults ? addResults.engError : null);
    setAltLanguageError(addResults ? addResults.altLanguageError : null);

    setShowAddDialog(!addResult.current);
  };

  /**
   * Event to handle when the add lookup dialog is closed.
   */
  const handleCloseAddLookup = () => {
    setShowAddDialog(false);
  };

  useEffect(() => {
    setPostTownEngLookup(
      lookupContext.currentLookups.postTowns
        .filter((x) => x.language === "ENG" && !x.historic)
        .sort(function (a, b) {
          return a.postTown.localeCompare(b.postTown, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        })
    );
  }, [lookupContext.currentLookups.postTowns]);

  useEffect(() => {
    setPostTownCymLookup(
      lookupContext.currentLookups.postTowns
        .filter((x) => x.language === "CYM" && !x.historic)
        .sort(function (a, b) {
          return a.postTown.localeCompare(b.postTown, undefined, {
            numeric: true,
            sensitivity: "base",
          });
        })
    );
  }, [lookupContext.currentLookups.postTowns]);

  useEffect(() => {
    setSubLocalityLookup(lookupContext.currentLookups.subLocalities.filter((x) => x.language === "ENG"));
  }, [lookupContext.currentLookups.subLocalities]);

  useEffect(() => {
    setActionData(
      data
        ? data
        : variant === "level"
        ? settingsContext.isScottish
          ? 0
          : ""
        : variant === "note"
        ? ""
        : ["excludeFromExport", "siteVisit"].includes(variant)
        ? false
        : null
    );
    setRepresentativePointCodeLookup(FilteredRepresentativePointCode(settingsContext.isScottish, true));
  }, [data, variant, settingsContext.isScottish]);

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        aria-labelledby="wizard-action-dialog-title"
        sx={{ p: "16px 16px 24px 16px", borderRadius: "9px" }}
        onClose={handleCancel}
      >
        <ADSDialogTitle title={getTitle()} closeTooltip="Cancel" onClose={handleCancel} />
        <DialogContent sx={{ backgroundColor: theme.palette.background.paper }}>{getContent()}</DialogContent>
        <DialogActions sx={{ justifyContent: "flex-start", pl: "24px", pb: "24px" }}>
          <Button variant="contained" onClick={handleOk} sx={blueButtonStyle} startIcon={<SaveIcon />}>
            Save
          </Button>
          <Button variant="contained" autoFocus sx={whiteButtonStyle} onClick={handleCancel} startIcon={<CloseIcon />}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <AddLookupDialog
        isOpen={showAddDialog}
        variant={lookupType}
        errorEng={engError}
        errorAltLanguage={altLanguageError}
        onDone={(data) => handleDoneAddLookup(data)}
        onClose={handleCloseAddLookup}
      />
    </>
  );
}

export default WizardActionDialog;
